import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  deleteDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface BetaUser {
  email: string;
  addedAt: Date;
  addedBy?: string;
  notes?: string;
}

export class BetaAccessService {
  private static COLLECTION = 'betaUsers';

  /**
   * Check if an email is in the beta allowlist
   */
  static async isEmailAllowed(email: string): Promise<boolean> {
    try {
      const docRef = doc(db, this.COLLECTION, email.toLowerCase());
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error('Error checking beta access:', error);
      return false;
    }
  }

  /**
   * Add an email to the beta allowlist
   */
  static async addBetaUser(email: string, addedBy?: string, notes?: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, email.toLowerCase());
      await setDoc(docRef, {
        email: email.toLowerCase(),
        addedAt: serverTimestamp(),
        addedBy: addedBy || 'system',
        notes: notes || ''
      });
    } catch (error) {
      console.error('Error adding beta user:', error);
      throw error;
    }
  }

  /**
   * Remove an email from the beta allowlist
   */
  static async removeBetaUser(email: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, email.toLowerCase());
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error removing beta user:', error);
      throw error;
    }
  }

  /**
   * Get all beta users
   */
  static async getAllBetaUsers(): Promise<BetaUser[]> {
    try {
      const q = query(collection(db, this.COLLECTION), orderBy('addedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          email: data.email,
          addedAt: data.addedAt?.toDate() || new Date(),
          addedBy: data.addedBy,
          notes: data.notes
        };
      });
    } catch (error) {
      console.error('Error getting beta users:', error);
      throw error;
    }
  }

  /**
   * Initialize beta allowlist with default users
   */
  static async initializeBetaUsers(): Promise<void> {
    const defaultBetaUsers = [
      { email: 'phil@makebetterbetter.com', notes: 'Admin - Google linking preferred' },
      { email: 'philipandrew@gmail.com', notes: 'Admin alt account - Google linking preferred' },
      { email: 'philiprajewski@yahoo.com', notes: 'Test account - Email/Pass' },
      { email: 'christopheraaronsnyder@gmail.com', notes: 'Beta tester - Google' }
    ];

    for (const user of defaultBetaUsers) {
      const exists = await this.isEmailAllowed(user.email);
      if (!exists) {
        await this.addBetaUser(user.email, 'system-init', user.notes);
        console.log(`Added beta user: ${user.email}`);
      }
    }
  }
}